import { useState, useRef } from 'react';
import { Upload, X, FileText, FileImage, File, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@components/ui';

export default function DocumentUpload({
    onUpload,
    maxSizeMB = 5,
    acceptedTypes = ".pdf,.jpg,.jpeg,.png",
    title = "Belge Yükle",
    description = "PDF veya Görsel dosyalarınızı buraya sürükleyin veya seçin"
}) {
    const [isDragging, setIsDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const validateFile = (selectedFile) => {
        if (!selectedFile) return false;

        // Boyut kontrolü
        const sizeInMB = selectedFile.size / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
            setError(`Dosya boyutu en fazla ${maxSizeMB}MB olabilir.`);
            return false;
        }

        setError('');
        return true;
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (validateFile(droppedFile)) {
            setFile(droppedFile);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (validateFile(selectedFile)) {
            setFile(selectedFile);
        }
    };

    const removeFile = () => {
        setFile(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUploadSubmit = () => {
        if (file && onUpload) {
            onUpload(file);
            // Başarılı upload simülasyonu sonrası temizle
            setTimeout(() => {
                removeFile();
            }, 1000);
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType.includes('pdf')) return <FileText size={24} className="text-error" />;
        if (fileType.includes('image')) return <FileImage size={24} className="text-primary" />;
        return <File size={24} className="text-text-muted" />;
    };

    return (
        <div className="w-full">
            {!file ? (
                <div
                    className={clsx(
                        "border-2 border-dashed rounded-xl p-6 md:p-8 text-center transition-all duration-200 cursor-pointer",
                        isDragging ? "border-primary bg-primary-50" : "border-border-light hover:border-primary hover:bg-bg-tertiary",
                        error && "border-error bg-error-50"
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept={acceptedTypes}
                        onChange={handleFileSelect}
                    />

                    <div className="w-12 h-12 rounded-full bg-bg-secondary flex items-center justify-center mx-auto mb-4 text-text-muted group-hover:text-primary transition-colors">
                        <Upload size={24} />
                    </div>

                    <h4 className="text-base font-semibold text-text-primary mb-1">{title}</h4>
                    <p className="text-sm text-text-tertiary">{description}</p>
                    <p className="text-xs text-text-muted mt-2">Maksimum boyut: {maxSizeMB}MB</p>

                    {error && (
                        <p className="text-sm text-error mt-3 font-medium">{error}</p>
                    )}
                </div>
            ) : (
                <div className="bg-bg-tertiary rounded-xl p-4 border border-border-light">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-bg-secondary flex items-center justify-center shrink-0">
                            {getFileIcon(file.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
                            <p className="text-xs text-text-tertiary">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <button
                            onClick={removeFile}
                            className="p-2 text-text-muted hover:text-error hover:bg-bg-secondary rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleUploadSubmit}
                            icon={<CheckCircle2 size={16} />}
                        >
                            Yükle
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
